import {useEffect, useMemo, useState} from 'react';
import { t } from 'utilities/TranslationUtils';
import { useStores } from 'index';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { DefaultRoutes } from 'Routes';

// components
import { AppPageHeader, AppSectionErrorBoundary, AppUseIcon, AppCopyText, AppLightTooltip } from 'components/ui';
import {AppButton} from 'components/form';
import {Menu, MenuItem, TableCell} from '@trackman/web-shared-components';
import { TableWithFilters } from 'components/Tables/TableWithFilters';
import { OrganizationsTableColumns, appTableFiltersInstance } from './OrganizationsTableColumns';

// types
import { IOrganization } from 'types/models/OrganizationModels';
import { AppLinkButton } from 'components/ui/AppLinkButton';
import { AddUpdateOrganizationDialog } from './components/AddUpdateOrganizationDialog';

export const OrganizationsPage: React.FunctionComponent = observer(() => {
  const history = useHistory();
  const { apiServiceAxiosInstance, organizationsStore, configurationService, analyticsStore, notificationStore } = useStores();

  const [showAddUpdateOrganizationDialog, setShowAddUpdateOrganizationDialog] = useState<boolean>(false);
  const [contextualDataUrl, setContextualDataUrl] = useState<string>('');
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState<IOrganization>(organizationsStore.GetEmptyOrganization());


  const handleOpeningActionsMenu = (event) => {
    setActionsMenuAnchorEl(event.currentTarget);
  };
  const handleClosingActionsMenu = () => {
    setActionsMenuAnchorEl(null);
  };
  const getContextualDataUrl = async () => {
    const configs = await configurationService.getConfigs();
    setContextualDataUrl(configs.contextualDataUrl);
  };

  useEffect(() => {
    organizationsStore.getOrganizations(apiServiceAxiosInstance);
    getContextualDataUrl();
    analyticsStore.sendPageView('OrganizationsPage');
  }, []);

  const rows = useMemo(() => organizationsStore.organizationsToShow, [organizationsStore.organizationsToShow]);

  const columns = useMemo(OrganizationsTableColumns, []);
  
  const renderCell = ({ cellValue, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {

    if (columnId === 'id') {
      return (
        <div className='d-flex justify-content-between align-items-center w-100'>
          <p data-test={`id-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
            {cellValue}
          </p>
          <AppCopyText className='ml-1 ' text={cellValue} />
        </div>
      );
    }

    if (columnId === 'name' || columnId === 'shortName') {
      return (
        <span className='overflow-ellipsis text-capitalize' title={`${cellValue?.trim() || '-'}`}>
          {cellValue?.trim() || '-'}
        </span>
      );
    }

    if (columnId === 'links') {
      return (
        <div>
          <AppButton
            variant='transparent'
            className='d-flex align-items-center justify-content-center'
            onClick={(e) => {
              handleOpeningActionsMenu(e);
              setSelectedOrganization(row);
            }}
            data-test={`actions-menu-button-${row.id}`}
          >
            <AppUseIcon name='MenuDotsH' color='currentColor' width='16' height='16' />
          </AppButton>
        </div>
      );
    }

    if (columnId === 'externalLinks') {
      return (
        <div className='d-flex'>
          <AppLightTooltip title={t('common.opens_new_window')}>
            <AppLinkButton text={t('organizationsPage.Contextual_d')} iconName='LargeExternalLinkIconSecondaryGrey' onClick={() => {
              window.open(`${contextualDataUrl}/Organizations/${row.id}/#Organization`, '_blank');
            }} />
          </AppLightTooltip>
        </div>
      );  
    }

    return cellValue || '-';
  };

  const rowMapper = (cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, value }) => (
          <TableCell {...getCellProps()} className='table-body-cell' align={column.align} onClick={() => { }}>
            {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
          </TableCell>
        ))}
      </>
    );
  };
  
  const openOrgSubPage = (route: string, organization: IOrganization | null = null, queryString: string | null = null) => {
    const org = selectedOrganization ?? organization;
    if (!org) {
      notificationStore.addToast(
        'Error',
        'Organization not selected',
        'error'
      );
      return;
    }

    history.push(`${route}/${org.id}${queryString ? queryString : ""}`);
  }

  const openAddUpdateOrganizationDialog = (clearSelectedOrg: boolean) => {
    if (clearSelectedOrg) {
      setSelectedOrganization(organizationsStore.GetEmptyOrganization());
    }
    setShowAddUpdateOrganizationDialog(true);
  };

  const addUpdateOrganizationSubmitHandler = async (organization: IOrganization) => {
    setShowAddUpdateOrganizationDialog(false);
    setSelectedOrganization(organizationsStore.GetEmptyOrganization());
    await organizationsStore.addOrUpdateOrganization(apiServiceAxiosInstance, organization);
  };

  const addUpdateOrganizationCloseHandler = () => {
    setShowAddUpdateOrganizationDialog(false);
    setSelectedOrganization(organizationsStore.GetEmptyOrganization());
  };

  return (
    <AppSectionErrorBoundary>
      <div>
        <AppPageHeader title={t('organizationsPage.organizations')}>
          <div className='d-flex justify-content-end gap-2 align-items-center'>
            <AppButton
              variant='transparent'
              disabled={organizationsStore.isLoading}
              data-test='export-to-csv-button'
              onClick={() => organizationsStore.exportOrganizationsToCsv()}
            >
              {t('common.exportToCsv')}
              <AppUseIcon name='SaveIcon' width='16' height='16' className='ml-2' />
            </AppButton>

            <AppButton
              variant='transparent'
              disabled={organizationsStore.isLoading}
              data-test='export-to-csv-button'
              onClick={() => openAddUpdateOrganizationDialog(true)}
            >
              {t('organizationsPage.addOrganization')}
              <AppUseIcon name='PlusIconWithoutBorder' width='14' height='14' className='ml-2' />
            </AppButton>
          </div>  
        </AppPageHeader>
        <TableWithFilters<IOrganization>
          appTableFiltersInstance={appTableFiltersInstance}
          rowMapper={rowMapper}
          columns={columns}
          data={rows}
          isLoading={organizationsStore.isLoading}
        />

        <Menu id='organization-actions' anchorEl={actionsMenuAnchorEl} open={Boolean(actionsMenuAnchorEl)} onClose={handleClosingActionsMenu}>
          <MenuItem
            onClick={() => {
              handleClosingActionsMenu();
              openAddUpdateOrganizationDialog(false);
            }}
          >
            {t('organizationsPage.updateOrganization')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClosingActionsMenu();
              openOrgSubPage(DefaultRoutes.ToursPage);
            }}
          >
            {t('organizationsPage.tours')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClosingActionsMenu();
              openOrgSubPage(DefaultRoutes.UsersPage);
            }}
          >
            {t('organizationsPage.users')}
          </MenuItem>
        </Menu>

        <AddUpdateOrganizationDialog
          onSubmit={addUpdateOrganizationSubmitHandler}
          onClose={addUpdateOrganizationCloseHandler}
          selectedOrganization={selectedOrganization ?? organizationsStore.GetEmptyOrganization()}
          isOpened={showAddUpdateOrganizationDialog}
        />
      </div>
    </AppSectionErrorBoundary>
  );
});

export default OrganizationsPage;
